import styled from '@emotion/styled';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { QuickLinkCard } from '@hotjar/activation-toolkit';
import { Text, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { Site } from '@hotjar/site';
import NpsSvg from './assets/nps.svg';
import FeebackSvg from './assets/feedback.svg';
import SurveySvg from './assets/survey.svg';

const QuickLinkCardWrapper = styled.div`
  margin-top: ${tokens.spacer.x2};
  display: flex;
  flex-flow: row wrap;
  gap: ${tokens.spacer.x3};
  margin-bottom: ${tokens.spacer.x6};
`;

const StyledQuickLinkCard = styled(QuickLinkCard)`
  max-width: calc(33.33% - 18px);
`;

const surveyTemplateLinkDetails = {
  nps: {
    id: '803182',
    activeFlowSection: 'name',
    category: 'All templates',
  },
  feedback: {
    id: '803650',
    activeFlowSection: 'name',
    category: 'All templates',
  },
  survey: {
    id: '847357',
    activeFlowSection: 'name',
    category: 'All templates',
  },
};

const isOdd = (num: number) => num % 2;

type QuickLinks = {
  id: string;
  title: string;
  description: string;
  href: string;
  image: string;
  isTCIRequired?: boolean;
}[];

export const QuickLinksSection = ({
  site,
  inGuidedSetupExperiment,
}: {
  site: Site;
  inGuidedSetupExperiment?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const currentSiteId = site?.id;
  const { click: trackClick } = useTrack();
  const quickLinks: QuickLinks = [
    {
      id: 'nps',
      title: t('activation:space-overview.quick-links-section.nps.title'),
      description: t('activation:space-overview.quick-links-section.nps.description'),
      href: getRoute(SURVEYS.CREATE.path, {
        siteId: currentSiteId as number,
        templateId: surveyTemplateLinkDetails.nps.id,
        activeFlowSection: surveyTemplateLinkDetails.nps.activeFlowSection,
        category: encodeURIComponent(surveyTemplateLinkDetails.nps.category),
      }),
      image: NpsSvg,
    },
    {
      id: 'feedback',
      title: t('activation:space-overview.quick-links-section.feedback.title'),
      description: t('activation:space-overview.quick-links-section.feedback.description'),
      href: getRoute(SURVEYS.CREATE.path, {
        siteId: currentSiteId as number,
        templateId: surveyTemplateLinkDetails.feedback.id,
        activeFlowSection: surveyTemplateLinkDetails.feedback.activeFlowSection,
        category: encodeURIComponent(surveyTemplateLinkDetails.feedback.category),
      }),
      image: FeebackSvg,
    },
    {
      id: 'survey',
      title: t('activation:space-overview.quick-links-section.survey.title'),
      description: t('activation:space-overview.quick-links-section.survey.description'),
      href: getRoute(SURVEYS.CREATE.path, {
        siteId: currentSiteId as number,
        templateId: surveyTemplateLinkDetails.survey.id,
        activeFlowSection: surveyTemplateLinkDetails.survey.activeFlowSection,
        category: encodeURIComponent(surveyTemplateLinkDetails.survey.category),
      }),
      image: SurveySvg,
    },
  ];

  return (
    <>
      <Text as="h3" textType="header5">
        {t('activation:space-overview.quick-links-section.title')}
      </Text>
      <QuickLinkCardWrapper>
        {quickLinks.map((quickLink, index) => (
          <StyledQuickLinkCard
            key={quickLink.id}
            title={quickLink.title}
            description={quickLink.description}
            href={quickLink.href}
            image={quickLink.image}
            backgroundColor={isOdd(index) ? 'magenta' : 'lilac'}
            onClick={() => trackClick(`${quickLink.id} link`, 'Quick Link Card', {})}
            alert={
              inGuidedSetupExperiment && quickLink?.isTCIRequired
                ? t('activation:space-overview.quick-links-section.requires-installation-alert')
                : undefined
            }
          />
        ))}
      </QuickLinkCardWrapper>
    </>
  );
};
